import Accordion from "../../../components/accordian"
import { useTranslation } from "react-i18next";
import { useCallback, useState } from "react";
import FileUploadInput from "../../../components/inputs/FileUploadInput";
import { allowedFileTypes, ContentType, PARKING_STICKER_REQUEST_ATTACHMENT_KEYS, PARKING_STICKER_REQUEST_ATTACHMENTS_TYPE, USER_PROFILE_ATTACHMENTS_TYPE } from "../../../constants/common";
import { Control, FieldErrors, UseFormClearErrors } from "react-hook-form";
import toast from "react-hot-toast";
import { useGetParkingEntryStickerFileByType } from "../../../hooks/permit/mutation";
import ViewAttachmentModal from "../../../components/modal/ViewAttachmentModal";

interface AttachmentsProps {
    isAttachments: {
        haveEmployeeCard: number;
        haveNationalId: number;
    }
    control: Control<{
        uploadEmployeeCard?: any;
        uploadNationalId?: any;
        name: string;
        department: string;
        jobTitle: string;
        mobileNo: string;
        officePhoneNo: string;
        employeeNumber: string;
    }, any>
    errors: FieldErrors<{
        uploadEmployeeCard?: any;
        uploadNationalId?: any;
        name: string;
        department: string;
        jobTitle: string;
        mobileNo: string;
        officePhoneNo: string;
        employeeNumber: string;
    }>
    clearErrors: UseFormClearErrors<{
        uploadEmployeeCard?: any;
        uploadNationalId?: any;
        name: string;
        department: string;
        jobTitle: string;
        mobileNo: string;
        officePhoneNo: string;
        employeeNumber: string;
    }>
    uploadRefs: Record<string, React.MutableRefObject<HTMLInputElement | null>>;
    fileNames: Record<string, string>;
    setFileName: (key: string, value: React.SetStateAction<string>) => void
    isFieldsDisabled: boolean;
    entryStickerDetails?: {
        id: number;
    }

    fileTypes: Record<string, string>;
    filesBase64: Record<string, string>
}

const Attachments = ({
    isAttachments,
    isFieldsDisabled,
    control,
    errors,
    uploadRefs,
    fileNames,
    setFileName,
    entryStickerDetails,
    fileTypes,
    filesBase64,
}: AttachmentsProps) => {
    const { t } = useTranslation();

    const [selectedKey, setSelectedKey] = useState<string>("1");

    const [previewFiles, setPreviewFiles] = useState<
        Record<number, { url: string; contentType: string }>
    >({});
    const [imageSrc, setImageSrc] = useState("");
    const [contentType, setContentType] = useState("");
    const [isViewAttachmentModalOpen, setIsViewAttachmentModalOpen] =
        useState(false);
    const [selectedType, setSelectedType] = useState<number>(0);
    const [isGetFileByTypeLoading, setIsGetFileByTypeLoading] = useState(false);

    const {
        mutateAsync: GetParkingEntryStickerFileByType,
        isPending: isGetParkingEntryStickerFileByTypeLoading
    } = useGetParkingEntryStickerFileByType();

    const handleAttachmentView = useCallback(
        async (type: number) => {
            try {
                // If we already have a local preview for this attachment type
                if (previewFiles && previewFiles[type]) {
                    setImageSrc(previewFiles[type].url);
                    setContentType(previewFiles[type].contentType);
                    setIsViewAttachmentModalOpen(true);
                    return;
                }

                if (entryStickerDetails?.id) {
                    setSelectedType(type);

                    const body = {
                        Id: entryStickerDetails?.id,
                        Type: type,
                    };

                    const res = await GetParkingEntryStickerFileByType(body);

                    setIsGetFileByTypeLoading(isGetParkingEntryStickerFileByTypeLoading);

                    if (res) {
                        let url = "";
                        if (res?.contentType !== ContentType.PDF) {
                            url = `data:${res?.contentType};base64,${res?.photoFile}`;
                        } else {
                            const binaryString = atob(res?.photoFile);
                            const byteArray = new Uint8Array(binaryString.length);

                            for (let i = 0; i < binaryString.length; i++) {
                                byteArray[i] = binaryString.charCodeAt(i);
                            }

                            const blob = new Blob([byteArray], { type: ContentType.PDF });
                            url = URL.createObjectURL(blob);
                        }
                        setImageSrc(url);
                        setContentType(res?.contentType);
                        setIsViewAttachmentModalOpen(true);
                    }

                    return;
                }

                setImageSrc(
                    type === PARKING_STICKER_REQUEST_ATTACHMENTS_TYPE.NATIONAL_ID
                        ? `data:${fileTypes.nationalId};base64,${filesBase64.nationalId}`
                        : `data:${fileTypes.hostEmployeeCard};base64,${filesBase64.hostEmployeeCard}`
                );
                setContentType(
                    type === PARKING_STICKER_REQUEST_ATTACHMENTS_TYPE.NATIONAL_ID
                        ? fileTypes.nationalId
                        : fileTypes.hostEmployeeCard
                );
                setIsViewAttachmentModalOpen(true);
            } catch (error) {
                toast.error("Something went wrong");
            } finally {
                setSelectedType(0);
            }
        },
        [
            previewFiles,
            entryStickerDetails,
            GetParkingEntryStickerFileByType,
            isGetParkingEntryStickerFileByTypeLoading,
            fileTypes,
            filesBase64,
        ],
    );

    return (
        <>
            <div className="bg-white z-10 p-2 md:p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
                <Accordion
                    id="1"
                    selectedKey={selectedKey}
                    onSelectionChange={setSelectedKey}
                    title={
                        <h1 className="text-xl md:text-2xl flex flex-1 -mt-2 items-center">
                            {t("4")}. {t("attachments")}
                        </h1>
                    }
                >
                    <div className="flex flex-col gap-2 lg:gap-4">
                        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 lg:gap-4">
                            <FileUploadInput
                                name="uploadNationalId"
                                label={t("uploadNationalID")}
                                accept=".png,.jpg,.jpeg,.pdf"
                                allowedTypes={allowedFileTypes}
                                isAttachments={isAttachments?.haveNationalId}
                                disabled={isFieldsDisabled}
                                control={control}
                                uploadRef={
                                    uploadRefs[
                                    PARKING_STICKER_REQUEST_ATTACHMENT_KEYS
                                        .NATIONAL_ID
                                    ]
                                }
                                fileName={
                                    fileNames[
                                    PARKING_STICKER_REQUEST_ATTACHMENT_KEYS
                                        .NATIONAL_ID
                                    ]
                                }
                                setFileName={(val) =>
                                    setFileName(
                                        PARKING_STICKER_REQUEST_ATTACHMENT_KEYS.NATIONAL_ID,
                                        val,
                                    )
                                }
                                handleAttachmentView={handleAttachmentView}
                                selectedType={selectedType}
                                attachmentType={
                                    PARKING_STICKER_REQUEST_ATTACHMENTS_TYPE.NATIONAL_ID
                                }
                                isGetFileByTypeLoading={isGetFileByTypeLoading}
                                error={errors.uploadNationalId}
                                previewFiles={previewFiles}
                                setPreviewFiles={setPreviewFiles}
                                isRemovable={false}
                            />

                            <FileUploadInput
                                name="uploadEmployeeCard"
                                label={t("uploadEmployeeCard")}
                                accept=".png,.jpg,.jpeg,.pdf"
                                allowedTypes={allowedFileTypes}
                                isAttachments={isAttachments?.haveEmployeeCard}
                                disabled={isFieldsDisabled}
                                control={control}
                                uploadRef={
                                    uploadRefs[
                                    PARKING_STICKER_REQUEST_ATTACHMENT_KEYS
                                        .EMPLOYEE_CARD
                                    ]
                                }
                                fileName={
                                    fileNames[
                                    PARKING_STICKER_REQUEST_ATTACHMENT_KEYS
                                        .EMPLOYEE_CARD
                                    ]
                                }
                                setFileName={(val) =>
                                    setFileName(
                                        PARKING_STICKER_REQUEST_ATTACHMENT_KEYS.EMPLOYEE_CARD,
                                        val,
                                    )
                                }
                                handleAttachmentView={handleAttachmentView}
                                selectedType={selectedType}
                                attachmentType={
                                    PARKING_STICKER_REQUEST_ATTACHMENTS_TYPE.EMPLOYEE_CARD
                                }
                                isGetFileByTypeLoading={isGetFileByTypeLoading}
                                error={errors.uploadEmployeeCard}
                                previewFiles={previewFiles}
                                setPreviewFiles={setPreviewFiles}
                                isRemovable={false}
                            />
                        </div>
                    </div>
                </Accordion>
            </div>

            <ViewAttachmentModal
                attachment={imageSrc}
                contentType={contentType}
                isOpen={isViewAttachmentModalOpen}
                onOpenChange={() => {
                    setIsViewAttachmentModalOpen(false);
                    setImageSrc("");
                }}
            />
        </>
    )
}

export default Attachments
